import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import * as Sentry from '@sentry/angular';
import { FirebaseError } from 'firebase/app';

import { Environment } from '@interfaces/environment';
import { BusinessUser } from '@models/business-user';
import { AppConfigService } from '@services/app-config.service';

@Injectable({
  providedIn: 'root'
})
export class SentryService {
  get env(): Environment {
    return this.appConfigService.environment;
  }

  get version(): string {
    return this.appConfigService.version;
  }

  constructor(private appConfigService: AppConfigService) {}

  captureException(error: Error & { originalError?: Error }): void {
    if (this.filterException(error)) {
      if (!this.env.SENTRY_DSN) {
        console.groupCollapsed('%cFiltered error', 'color: #fff; background-color: #f00;font-weight: bold;');
        console.error(error);
        console.groupEnd();
      }
      return;
    }

    if (this.env.SENTRY_DSN) {
      Sentry.captureException(error.originalError || error);
    } else {
      console.error(error);
    }
  }

  cleanUser(): void {
    if (this.env.SENTRY_DSN) {
      Sentry.getCurrentScope().setUser(null);
    }
  }

  init(): void {
    if (this.env.SENTRY_DSN) {
      Sentry.init({
        integrations: [
          Sentry.browserTracingIntegration({
            beforeStartSpan(options) {
              return {
                ...options,
                name: options.name
                  .replace(/\?.*/g, '?[queryParams]') // On remplace les queryParams (/module?myQuery=param -> /module?[queryParams])
                  .replace(/\/\d+/g, '/{id}') // On remplace les ids (/company/1234/module -> /company/{id}/module)
              };
            }
          }),
          Sentry.replayIntegration({
            maskAllText: false,
            maskAllInputs: false,
            blockAllMedia: false
          }),
          // Permet de corriger les chemins des fichiers sources dans les traces
          Sentry.rewriteFramesIntegration()
        ],
        tracePropagationTargets: [this.env.API_URL],
        tracesSampleRate: 0.15,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1.0,
        dsn: this.env.SENTRY_DSN,
        environment: this.env.ENV_NAME,
        release: this.version,
        beforeSend(event, hint) {
          const error = hint.originalException;
          if (error && error instanceof HttpErrorResponse) {
            return null;
          }
          return event;
        }
      });
    }
  }

  setUserFromBusinessUser(businessUser: BusinessUser): void {
    if (this.env.SENTRY_DSN) {
      Sentry.getCurrentScope().setUser({
        id: String(businessUser.id),
        username: businessUser.fullName,
        email: businessUser.email
      });
    }
  }

  addBreadcrumb(message: string, data: any = undefined, category: string = 'User action'): void {
    if (!this.env.SENTRY_DSN) {
      return;
    }
    Sentry.addBreadcrumb({
      message,
      data,
      category,
      level: 'info'
    });
  }

  private filterException(error: Error): boolean {
    if (error instanceof FirebaseError) {
      switch (error.code) {
        case 'messaging/permission-blocked':
          return true;
      }
    }

    return false;
  }
}
